/**
 * Reusable format functions for display across pages/components.
 * Import only what you need: import { formatFullName, formatPhone } from '../utils/formatters';
 */

const EMPTY_PLACEHOLDER = "—";

/**
 * Unwrap backend strings like "\"suresh\"" to "suresh" (removes outer quotes).
 * @param {*} v - Value (string, number, etc.)
 * @returns {string|null} Unwrapped string or original value
 */
export const unwrapString = (v) => {
  if (v == null) return v;
  const s = String(v).trim();
  if (s.length >= 2 && s[0] === '"' && s[s.length - 1] === '"') {
    return s.slice(1, -1).replace(/\\"/g, '"');
  }
  return s;
};

/**
 * Pascal/title case: capitalize first letter of each word
 * @param {string} val - Input value
 * @returns {string} Formatted string or original if empty
 */
export const formatPascal = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  return String(val)
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Business name: Pascal case per word, keeping all-caps acronyms
 * ("getwell bee" -> "Getwell Bee", "ABC corp" -> "ABC Corp").
 * @param {string} val
 * @returns {string}
 */
export const formatBusinessName = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  return String(val)
    .trim()
    .split(/\s+/)
    .map((w) => {
      const letters = w.replace(/[^A-Za-z]/g, "");
      if (letters.length > 1 && letters === letters.toUpperCase()) return w;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
};

/** Identity fields that should be Pascal-cased on save. */
export const PERSON_NAME_KEYS = [
  "First_Name",
  "Last_Name",
  "Company_Name",
  "College_Name",
  "Contact_Name",
  "Primary_Contact_Name",
  "Staff_Name",
  "Supervisor_Name",
  "first_name",
  "last_name",
  "company_name",
  "college_name",
  "contact_name",
  "primary_contact_name",
  "staff_name",
  "supervisor_name",
  "firstName",
  "lastName",
  "companyName",
  "collegeName",
  "contactName",
  "primaryContactName",
  "staffName",
  "supervisorName",
];

/**
 * Mutate (or copy) an object so person/company name fields are Pascal-cased.
 * Empty / null / undefined values are left unchanged.
 * @param {object} obj
 * @param {string[]} [keys]
 * @returns {object}
 */
export const applyPascalNameFields = (obj, keys = PERSON_NAME_KEYS) => {
  if (!obj || typeof obj !== "object") return obj;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const v = obj[key];
    if (v == null || v === "") continue;
    obj[key] = formatPascal(v);
  }
  return obj;
};

/**
 * Full name: First Name, Last Name with Pascal case each (e.g. "John, Doe")
 * @param {string} nameStr - Combined "FirstName LastName" string
 * @param {string} [firstName] - Optional first name (if available separately)
 * @param {string} [lastName] - Optional last name (if available separately)
 * @returns {string} Formatted "FirstName, LastName"
 */
export const formatFullName = (nameStr, firstName, lastName) => {
  if (firstName != null || lastName != null) {
    const fn = formatPascal(String(firstName || "").trim());
    const ln = formatPascal(String(lastName || "").trim());
    const result = [fn, ln].filter(Boolean).join(", ");
    return result || EMPTY_PLACEHOLDER;
  }
  if (!nameStr || nameStr === EMPTY_PLACEHOLDER) return nameStr;
  const parts = String(nameStr).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return EMPTY_PLACEHOLDER;
  if (parts.length === 1) return formatPascal(parts[0]);
  const fname = formatPascal(parts[0]);
  const lname = formatPascal(parts.slice(1).join(" "));
  return `${fname}, ${lname}`;
};

/**
 * Phone format
 * - With country code: +91 923-546-7257
 * - Without code:       923-546-7257
 * @param {string} val - Phone number (digits or mixed)
 * @returns {string} Formatted phone or original if < 10 digits
 */
export const formatPhone = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  const raw = String(val).trim();

  // Strip all non-digits for consistent parsing
  const allDigits = raw.replace(/\D/g, "");
  if (!allDigits) return raw;

  // Last 10 digits are treated as the local number, any leading digits are country code
  const localDigits = allDigits.slice(-10);
  const countryDigits = allDigits.length > 10 ? allDigits.slice(0, allDigits.length - 10) : "";

  let local = "";
  if (localDigits.length <= 3) {
    local = localDigits;
  } else if (localDigits.length <= 6) {
    local = `${localDigits.slice(0, 3)}-${localDigits.slice(3)}`;
  } else {
    // 7–10 digits: 3-3-rest (e.g. 976-647-67554)
    local = `${localDigits.slice(0, 3)}-${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
  }

  const countryCode = countryDigits ? `+${countryDigits}` : "";
  return countryCode ? `${countryCode} ${local}` : local;
};

/**
 * Email: always lowercase for display
 * @param {string} val - Email address
 * @returns {string} Lowercase email
 */
export const formatEmail = (val) => {
  if (!val || val === EMPTY_PLACEHOLDER) return val;
  return String(val).trim().toLowerCase();
};

/**
 * Map a service full name to a short code (e.g. "Search Engine Optimization" -> "SEO").
 * Falls back to an acronym built from the first letter of each word.
 * @param {string} name - Service display name from API / DB
 * @returns {string} Short code for compact table display
 */
export const formatServiceShortcode = (name) => {
  if (!name || name === EMPTY_PLACEHOLDER) return name;
  const raw = String(name).trim();
  if (!raw) return raw;

  const lower = raw.toLowerCase();

  // Explicit mappings for common services
  const map = {
    "search engine optimization": "SEO",
    "seo": "SEO",
    "search engine marketing": "SEM",
    "sem": "SEM",
    "social media marketing": "SMM",
    "smm": "SMM",
    "social media optimization": "SMO",
    "smo": "SMO",
    "web development": "WEB",
    "website development": "WEB",
    "web design": "WEB",
    "web application development": "WAP",
    "wap": "WAP"
  };

  if (map[lower]) return map[lower];

  // Heuristic fallbacks based on keywords
  if (lower.includes("search engine") && lower.includes("optimization")) return "SEO";
  if (lower.includes("search engine") && lower.includes("marketing")) return "SEM";
  if (lower.includes("social media") && lower.includes("marketing")) return "SMM";
  if (lower.includes("social media") && lower.includes("optimization")) return "SMO";
  if (lower.includes("web application")) return "WAP";
  if (lower.includes("web")) return "WEB";

  // Generic acronym from first letters of words
  const acronym = raw
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("");

  return acronym || raw;
};

/**
 * Look up a service short code from the backend `/service/categories` structure.
 * Falls back to the original `serviceName` when no explicit code is found.
 *
 * This is used so that table columns display the SAME short code that the
 * backend defines (e.g. "BANNER") instead of a best‑guess acronym ("B").
 *
 * @param {string} serviceName - Display name coming from lead/customer record
 * @param {Array} servicesData - Full categories array from `/service/categories`
 * @returns {string} Backend-defined shortcode or the original name
 */
export const getServiceShortcodeFromCategories = (serviceName, servicesData) => {
  if (!serviceName || serviceName === EMPTY_PLACEHOLDER) return serviceName;
  if (!Array.isArray(servicesData) || servicesData.length === 0) {
    return serviceName;
  }

  const normalizedServiceName = String(serviceName).trim().toLowerCase();
  if (!normalizedServiceName) return serviceName;

  for (const category of servicesData) {
    const subs = Array.isArray(category?.subcategories)
      ? category.subcategories
      : [];

    const subcategory = subs.find((sub) => {
      const subName =
        sub.subcategory_name ||
        sub.Subcategory_Name ||
        sub.name ||
        "";
      return subName.trim().toLowerCase() === normalizedServiceName;
    });

    if (subcategory) {
      const code =
        subcategory.Subcategory_Code ||
        subcategory.subcategory_code ||
        subcategory.Subcategory_Shortcode ||
        subcategory.subcategory_shortcode ||
        subcategory.Shortcode ||
        subcategory.shortcode ||
        subcategory.Service_Code ||
        subcategory.service_code ||
        subcategory.Code ||
        subcategory.code;

      if (code && String(code).trim() !== "") {
        return String(code).trim();
      }

      // No explicit code field – fall back to name as given by backend
      return serviceName;
    }
  }

  // No match – return the original so we never invent a wrong code
  return serviceName;
};

/**
 * Normalize `/tenants` list item for dropdowns (keeps type + name fields for {@link getCustomerSelectLabel}).
 * @param {object} item - API row `{ client }` or flat client
 * @returns {{ id: *, Customer_Type?: *, customer_type?: *, First_Name?: *, Last_Name?: *, Company_Name?: *, name: string, Owner_Company_Email?: *, Portal_Enabled?: boolean, isTenant?: boolean }|null}
 */
export function mapCustomerListItemFromApi(item) {
  const c = item?.client || item;
  const id = c?.client_id ?? c?.tenant_id ?? c?.id;
  if (id == null || id === "") return null;
  const personName = `${c.First_Name || c.first_name || ""} ${c.Last_Name || c.last_name || ""}`.trim();
  const ownerLogin = String(c.Owner_Company_Email || "").trim();
  const portalRaw = c.Portal_Enabled ?? c.portal_enabled;
  const portalEnabled =
    portalRaw === true || portalRaw === 1 || portalRaw === "1" || String(portalRaw).toLowerCase() === "true";
  const isTenant = Boolean(ownerLogin) || portalEnabled;
  return {
    id,
    Customer_Type: c.Customer_Type,
    customer_type: c.customer_type,
    First_Name: c.First_Name,
    first_name: c.first_name,
    Last_Name: c.Last_Name,
    last_name: c.last_name,
    Company_Name: c.Company_Name,
    company_name: c.company_name,
    companyName: c.companyName,
    Owner_Company_Email: ownerLogin || null,
    Portal_Enabled: portalEnabled,
    Status: c.Status || c.status || null,
    status: c.Status || c.status || null,
    isTenant,
    name:
      personName ||
      c.Customer_Name ||
      c.customer_name ||
      c.Company_Name ||
      c.company_name ||
      "—"
  };
}

/** Active CRM tenant/client (excludes Inactive). */
export function isActiveCustomer(c) {
  if (!c) return false;
  const status = String(c.Status || c.status || "").trim().toLowerCase();
  return status === "active" || status === "";
}

/** Portal / Cloud Connect org (tenant) vs plain CRM client. */
export function isPortalTenantCustomer(c) {
  if (!c) return false;
  if (typeof c.isTenant === "boolean") return c.isTenant;
  if (String(c.Owner_Company_Email || "").trim()) return true;
  const portalRaw = c.Portal_Enabled ?? c.portal_enabled;
  return portalRaw === true || portalRaw === 1 || portalRaw === "1" || String(portalRaw).toLowerCase() === "true";
}

/**
 * Label for customer dropdowns: Organization → company name; Individual (or unknown) → person name.
 * @param {object} c - Row from {@link mapCustomerListItemFromApi} or API customer object
 * @returns {string}
 */
export function getCustomerSelectLabel(c) {
  if (c == null) return "—";
  const typeRaw = String(c.Customer_Type || c.customer_type || "").trim().toLowerCase();
  const isOrg =
    typeRaw === "organization" ||
    typeRaw === "organisation" ||
    typeRaw === "org";

  const company = String(c.Company_Name || c.company_name || c.companyName || "").trim();
  const first = String(c.First_Name || c.first_name || "").trim();
  const last = String(c.Last_Name || c.last_name || "").trim();
  const person = [first, last].filter(Boolean).join(" ").trim();
  const legacyName = String(c.name || c.Customer_Name || c.customer_name || "").trim();

  if (isOrg) {
    if (company) return company;
    return person || legacyName || "—";
  }
  if (person) return person;
  if (legacyName) return legacyName;
  if (company) return company;
  return "—";
}

/**
 * Lowercase string for filtering customer dropdowns (matches company, person name, and display label).
 * @param {object} c
 * @returns {string}
 */
export function getCustomerSelectSearchText(c) {
  if (c == null) return "";
  const label = getCustomerSelectLabel(c);
  const person = `${c.First_Name || c.first_name || ""} ${c.Last_Name || c.last_name || ""}`.trim();
  const company = String(c.Company_Name || c.company_name || c.companyName || "").trim();
  const legacy = String(c.name || "").trim();
  return [label, person, company, legacy].filter(Boolean).join(" ").toLowerCase();
}

const INDIA_STATE_CODES = {
  "andhra pradesh": "AP",
  "arunachal pradesh": "AR",
  assam: "AS",
  bihar: "BR",
  chhattisgarh: "CG",
  goa: "GA",
  gujarat: "GJ",
  haryana: "HR",
  "himachal pradesh": "HP",
  jharkhand: "JH",
  karnataka: "KA",
  kerala: "KL",
  "madhya pradesh": "MP",
  maharashtra: "MH",
  manipur: "MN",
  meghalaya: "ML",
  mizoram: "MZ",
  nagaland: "NL",
  odisha: "OD",
  orissa: "OD",
  punjab: "PB",
  rajasthan: "RJ",
  sikkim: "SK",
  "tamil nadu": "TN",
  tamilnadu: "TN",
  telangana: "TG",
  tripura: "TR",
  "uttar pradesh": "UP",
  uttarakhand: "UK",
  "west bengal": "WB",
  delhi: "DL",
  "new delhi": "DL",
  "jammu and kashmir": "JK",
  "jammu & kashmir": "JK",
  ladakh: "LA",
  puducherry: "PY",
  pondicherry: "PY",
  chandigarh: "CH",
  "andaman and nicobar islands": "AN",
  "dadra and nagar haveli and daman and diu": "DH",
  lakshadweep: "LD",
};

const US_STATE_CODES = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY",
  "district of columbia": "DC",
};

/** India maps to "" so it is omitted from display. */
const COUNTRY_CODES = {
  india: "", bharat: "", ind: "",
  "united states": "USA", "united states of america": "USA", usa: "USA", us: "USA", america: "USA",
  "united kingdom": "UK", uk: "UK", england: "UK", "great britain": "UK", britain: "UK",
  "united arab emirates": "UAE", uae: "UAE", dubai: "UAE",
  canada: "CAN", australia: "AUS", "new zealand": "NZL", singapore: "SGP",
  germany: "DEU", france: "FRA", ireland: "IRL", netherlands: "NLD",
  "saudi arabia": "KSA", ksa: "KSA", qatar: "QAT", oman: "OMN", kuwait: "KWT", bahrain: "BHR",
  malaysia: "MYS", nepal: "NPL", "sri lanka": "LKA", bangladesh: "BGD",
  pakistan: "PAK", china: "CHN", japan: "JPN",
};

const toTitleWords = (s) =>
  String(s)
    .trim()
    .split(/\s+/)
    .map((w) =>
      /^[A-Z]{2,4}$/.test(w)
        ? w
        : w
            .toLowerCase()
            .replace(/(^|[-'(])([a-z])/g, (_, p, c) => p + c.toUpperCase())
    )
    .join(" ");

const normKey = (s) => String(s).trim().toLowerCase().replace(/\.+/g, "").replace(/\s+/g, " ");

/** Peel trailing state/country words off a single space-separated part ("hyderabad telangana india"). */
function peelSuffixes(words, lookup) {
  for (let n = Math.min(6, words.length - 1); n >= 1; n -= 1) {
    const key = normKey(words.slice(-n).join(" "));
    if (Object.prototype.hasOwnProperty.call(lookup, key)) {
      return { rest: words.slice(0, -n), key };
    }
  }
  return null;
}

/**
 * Display a free-text location as "City, ST" (India omitted) or "City, ST, USA".
 * e.g. "hyderabad, telangana, india" -> "Hyderabad, TG"; "austin texas usa" -> "Austin, TX, USA".
 * @param {string} val
 * @returns {string}
 */
export function formatLocation(val) {
  const raw = String(val ?? "").trim();
  if (!raw || raw === EMPTY_PLACEHOLDER) return raw;

  let parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  let countryKey = null;
  let stateName = null;

  if (parts.length === 1) {
    let words = parts[0].split(/\s+/);
    const c = peelSuffixes(words, COUNTRY_CODES);
    if (c) {
      countryKey = c.key;
      words = c.rest;
    }
    const s = peelSuffixes(words, { ...US_STATE_CODES, ...INDIA_STATE_CODES });
    if (s) {
      stateName = s.key;
      words = s.rest;
    }
    parts = [words.join(" ")];
  } else {
    const rest = [parts[0]];
    parts.slice(1).forEach((p) => {
      const key = normKey(p);
      if (countryKey == null && Object.prototype.hasOwnProperty.call(COUNTRY_CODES, key)) {
        countryKey = key;
      } else if (
        stateName == null &&
        (INDIA_STATE_CODES[key] || US_STATE_CODES[key] || /^[a-z]{2}$/.test(key))
      ) {
        stateName = key;
      } else {
        rest.push(p);
      }
    });
    parts = rest;
  }

  const countryCode = countryKey == null ? "" : COUNTRY_CODES[countryKey];
  const isUs = countryCode === "USA";
  let stateCode = "";
  if (stateName) {
    stateCode =
      (isUs ? US_STATE_CODES[stateName] : INDIA_STATE_CODES[stateName]) ||
      INDIA_STATE_CODES[stateName] ||
      US_STATE_CODES[stateName] ||
      stateName.toUpperCase();
  }

  return [...parts.map(toTitleWords), stateCode, countryCode].filter(Boolean).join(", ");
}
